/**
 * The document context, shared down the chat tree — what the old web's
 * `DocumentContextProvider` did. The chips in the transcript and the editor's
 * node view ask it one question ("does this item have parameters?") and the
 * send path asks it for enrichment; neither should be threaded through props.
 *
 * The default value answers "unknown" to everything, so a chip rendered
 * outside a provider paints the optimistic blue rather than throwing.
 */
import { createContext, useContext } from "react";
import type { DocumentContextItem } from "../lib/documentContext";
import type { MentionEnrichmentSource } from "../lib/mentionUtils";

export interface DocumentContextValue {
  /** Whether the (type, id) item carries parameters — `null` when not cached. */
  extractable: (type: string, id: string) => boolean | null;
  enrichment: MentionEnrichmentSource;
  /**
   * The full item, for the parameter picker (#58 item 3) — the reference's
   * `getItemDetail`.
   *
   * Async and lazy on purpose. `extractable` answers from cache because it runs
   * on every chip during render and must never block a paint; this runs once,
   * when a picker opens, and may have to fetch the type's items first. A chip
   * for a type the user has not browsed to is the normal case, not the edge.
   */
  item: (type: string, id: string) => Promise<DocumentContextItem | null>;
}

export const DEFAULT_DOCUMENT_CONTEXT: DocumentContextValue = {
  extractable: () => null,
  enrichment: { lookup: () => null },
  // Outside a provider there is no document to ask; the picker shows its
  // "no parameters" note rather than spinning forever.
  item: () => Promise.resolve(null),
};

const Ctx = createContext<DocumentContextValue>(DEFAULT_DOCUMENT_CONTEXT);

export const DocumentContextProvider = Ctx.Provider;

export function useDocumentContextCtx(): DocumentContextValue {
  return useContext(Ctx);
}
